import { useClientPortalAPI } from "@certn/api";
import { Loader } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext } from "react";

import { OrderingCheckCard } from "../../../../../../components/ordering-check-card";
import { OrderingUmbrellaSelectOption } from "../../../../../../components/ordering-check-card/types";
import { syncMutuallyInclusiveAddOns } from "../../../../../../pages/order/order-utils";
import { getInheritedAddOnArgs } from "../../../../../../pages/order/steps/components/useCheckSectionData";
import { CheckData } from "../../../../types";
import { PackageContext } from "../../../PackageContext";
import {
  getAvailableSelectOptions,
  getCheckPrice,
  getRequiredChecksFromSelectedChecks,
  getSelectedChecksExclMutuallyExclusiveInclRequired,
  getSelectedChecksExclRemovedExclStandalone,
  getUpdatedChecks,
  isCheckStandaloneDisabled,
} from "../../../package-utils";

export function CheckSection() {
  const { packageData, setPackage } = useContext(PackageContext);
  const { data: availableChecks, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/available-checks/`,
    packageData.is_applicant_pay ? { is_applicant_pay: "true" } : undefined
  );

  const bundleChecks = packageData.bundles.flatMap(
    ({ checks }) => checks?.map((check) => check.type) || []
  );

  const handleCheckSelect = (check: CheckData) => {
    if (packageData.checks.find(({ key }) => key === check.type)) {
      setPackage({
        ...packageData,
        checks: getSelectedChecksExclRemovedExclStandalone(
          packageData.checks,
          check
        ),
      });
      return;
    }

    // If the check has options, then automatically select the first option
    const firstOption = getAvailableSelectOptions(check)?.[0];
    const requiredChildChecks = firstOption?.required_check_types || [];
    const requiredChecksToOrder = [
      ...(check.required_check_types || []),
      ...requiredChildChecks,
    ];
    setPackage({
      ...packageData,
      checks: [
        ...getSelectedChecksExclMutuallyExclusiveInclRequired(
          availableChecks || [],
          packageData.checks,
          check,
          requiredChecksToOrder
        ),
        {
          key: check.type,
          name: check.name,
          check_arguments: getInheritedAddOnArgs(
            packageData.checks,
            check.add_on_options
          ),
          cost: getCheckPrice(check, firstOption),
          currency: check.currency,
          children: check.children,
          required_check_types: requiredChecksToOrder,
          must_be_ordered_with_one_of: check.must_be_ordered_with_one_of,
          checkDetails: check,
          ...(firstOption ? { selectedOptions: [firstOption] } : {}),
          add_on_options: check.add_on_options?.map((addon) => ({
            key: addon.key,
            name: addon.name ?? addon.label,
            price: addon.price,
            currency: addon.currency,
          })),
        },
      ],
    });
  };

  /**
   * Updates only check_arguments for a given check without recalculating cost or
   * touching selectedOptions. Used for add-on toggles (e.g. CV Review) where the
   * selection/pricing state should remain unchanged.
   * Also syncs mutually inclusive add-on state to sibling checks that declare
   * the same add-on key.
   */
  const handleUpdateCheckAddOns = (
    checkType: string,
    checkArguments: Record<string, unknown>
  ) => {
    const updated = packageData.checks.map((c) =>
      c.key === checkType ? { ...c, check_arguments: checkArguments } : c
    );
    setPackage({
      ...packageData,
      checks: syncMutuallyInclusiveAddOns(updated, checkType, checkArguments),
    });
  };

  const handleUpdateArguments = (
    check_arguments: Record<string, unknown>,
    check: CheckData,
    selectedOptions?: OrderingUmbrellaSelectOption[]
  ) => {
    setPackage({
      ...packageData,
      checks: [
        ...getUpdatedChecks(
          check_arguments,
          availableChecks || [],
          packageData.checks,
          check,
          selectedOptions
        ),
      ],
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      {availableChecks &&
        availableChecks.map((check) => {
          const isCheckRequired = getRequiredChecksFromSelectedChecks(
            packageData.checks
          ).includes(check.type);
          const isCheckInSelectedBundle = bundleChecks.includes(check.type);
          const isCheckSelected = !!packageData.checks.find(
            ({ key }) => key === check.type
          );
          const onUpdateSelection = (
            checkArguments: Record<string, unknown>,
            selectedOptions: OrderingUmbrellaSelectOption[] | undefined
          ) => {
            handleUpdateArguments(checkArguments, check, selectedOptions);
          };
          const checkArguments = packageData.checks.find(
            (packageCheck) => packageCheck.key === check.type
          )?.check_arguments;
          const currentSelection = check.select_options?.find(
            (option) =>
              option.value === checkArguments?.[option.select_field_argument]
          );

          return (
            <OrderingCheckCard
              isDisabled={
                isCheckInSelectedBundle ||
                isCheckStandaloneDisabled(packageData.checks, check)
              }
              checkOrderingError={
                check.check_has_ordering_error_for_customer_group
              }
              key={check.type}
              type={check.type}
              name={check.name}
              price={check.price}
              priceIsCatalogMinimum={check.price_is_catalog_minimum}
              currency={check.currency}
              checkChildren={check.children || []}
              turnaroundTime={check.estimated_turnaround_time}
              disclaimersIconList={check.disclaimers_icon_list}
              disclaimers={check.disclaimers}
              description={check.description}
              infoParagraphs={check.info_paragraphs}
              coverage={check.coverage ?? ""}
              keyFeatures={check.key_features}
              howItCanHelpYou={check.how_it_can_help_you}
              selectFieldDescription={check.select_field_descriptions}
              selectFieldEndpoint={check.select_field_endpoint}
              selectOptions={check.select_options}
              onUpdateSelection={onUpdateSelection}
              handleUpdateCheckAddOns={(args) =>
                handleUpdateCheckAddOns(check.type, args)
              }
              umbrellaChecksSupported={
                check.children?.filter((child) => {
                  const errorType =
                    child.check_has_ordering_error_for_customer_group?.type;
                  return errorType !== "error" && errorType !== "warning";
                }) || []
              }
              umbrellaChecksHeading={check.umbrella_child_checks_heading}
              isSelected={isCheckSelected}
              onPress={() => handleCheckSelect(check)}
              testID={slugify(check.type)}
              isRequiredCheck={isCheckRequired}
              width={480}
              selectedOptions={
                currentSelection
                  ? Array.isArray(currentSelection)
                    ? currentSelection
                    : [currentSelection]
                  : undefined
              }
              selectFieldInformation={check.select_field_information}
              checkArguments={checkArguments}
              isApplicantPay={packageData.is_applicant_pay}
              addOnOptions={check.add_on_options}
            />
          );
        })}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
  }),
};
